import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AddProjectModal } from "@/components/AddProjectModal";

type Project = {
  id: number;
  owner: string;
  name: string;
  created_at: string;
  review_enabled: boolean;
  babysit_enabled: boolean;
};

type ProjectPatch = Partial<
  Pick<Project, "review_enabled" | "babysit_enabled">
>;

// ProjectsSection lists configured projects with per-project workflow
// toggles (Review PRs / Babysit PRs) and exposes a `+` button that
// opens AddProjectModal. Lives on the Settings page.
//
// Auto-approve is a process-wide setting (AUTO_APPROVE env var /
// review.policy.auto_approve_enabled in config.yaml), not per-repo —
// see SPEC §5 + §6.
//
// Refresh behavior: list is fetched on mount, refreshed after every
// successful add. Toggle changes PATCH the server and optimistically
// update local state — a failure rolls the row back and surfaces the
// error inline. No polling — the list is operator-curated and
// changes infrequently.
export function ProjectsSection() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowError, setRowError] = useState<Record<number, string>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/projects");
      if (!res.ok) throw new Error(await res.text());
      setProjects(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const patchProject = useCallback(
    async (project: Project, patch: ProjectPatch) => {
      const previous = project;
      const optimistic = { ...project, ...patch };
      setProjects((rows) =>
        rows.map((r) => (r.id === project.id ? optimistic : r)),
      );
      setRowError((m) => {
        const { [project.id]: _omit, ...rest } = m;
        return rest;
      });
      try {
        const res = await fetch(`/api/projects/${project.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        if (!res.ok) throw new Error(await res.text());
        const updated: Project = await res.json();
        setProjects((rows) =>
          rows.map((r) => (r.id === project.id ? updated : r)),
        );
      } catch (e) {
        // Roll back the optimistic update so the UI matches server state.
        setProjects((rows) =>
          rows.map((r) => (r.id === project.id ? previous : r)),
        );
        setRowError((m) => ({
          ...m,
          [project.id]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [],
  );

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Projects</h2>
        <Button
          variant="outline"
          size="sm"
          aria-label="Add project"
          onClick={() => setModalOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && !error && projects.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No projects configured yet. Click <em>Add</em> to pick a repo.
        </p>
      )}

      {projects.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {projects.map((p) => (
            <li key={p.id} className="space-y-3 px-3 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono">
                  {p.owner}/{p.name}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <ToggleRow
                  label="Review PRs"
                  description="Enable PullRequest.Review workflow"
                  checked={p.review_enabled}
                  onCheckedChange={(checked) =>
                    void patchProject(p, { review_enabled: checked })
                  }
                />
                <ToggleRow
                  label="Babysit PRs"
                  description="Enable PullRequest.Babysit workflow"
                  checked={p.babysit_enabled}
                  onCheckedChange={(checked) =>
                    void patchProject(p, { babysit_enabled: checked })
                  }
                />
              </div>
              {rowError[p.id] && (
                <p className="text-xs text-destructive">{rowError[p.id]}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      <AddProjectModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onAdded={() => {
          void reload();
        }}
      />
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2">
      <div className="space-y-0.5">
        <div className="text-sm font-medium leading-none">{label}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </label>
  );
}
